/**
 * Cameras on the map by mount (pure). The crew calls them Manned (goes with its operator),
 * Static (fixed: position + heading + lens → the FOV wedge matters most) and Aerial (on an
 * aircraft). Legacy 'manual' mounts are treated as Static.
 */
import type { Camera, CameraMount, LatLon } from '@apos/shared';
import { angleDelta, bearing, distance } from './geometry';

export type CameraKind = 'static' | 'manned' | 'aerial';

export function cameraKind(mount: CameraMount | null | undefined): CameraKind {
  if (mount === 'crew') return 'manned';
  if (mount === 'aircraft') return 'aerial';
  return 'static';
}

export const CAMERA_KIND_LABEL: Record<CameraKind, string> = { static: 'Static', manned: 'Manned', aerial: 'Aerial' };

/** Wedge range bounds (m): long enough to read the pointing, short enough not to paint the map. */
export const WEDGE_MIN_M = 400;
export const WEDGE_MAX_M = 8000;
/** Used when nothing tells us how far the camera is looking. */
export const WEDGE_DEFAULT_M = 1500;

/**
 * How far to draw a FOV wedge from `origin`: a little past the subject when the camera is
 * pointing at it (inside the FOV plus a 10° margin), else a little past the mission centre, else
 * WEDGE_DEFAULT_M. Clamped to [WEDGE_MIN_M, WEDGE_MAX_M] and rounded to 100 m so it does not
 * shimmer as the subject moves. Pointing away from the subject never paints kilometres of map.
 */
export function wedgeRangeM(origin: LatLon, subject: LatLon | null, missionCenter: LatLon | null, view?: { headingDeg: number; hfovDeg: number }): number {
  const clamp = (d: number, max = WEDGE_MAX_M) => Math.round(Math.min(max, Math.max(WEDGE_MIN_M, d * 1.2)) / 100) * 100;
  if (subject) {
    const d = distance(origin, subject);
    const inView = !view || Math.abs(angleDelta(view.headingDeg, bearing(origin, subject))) <= view.hfovDeg / 2 + 10;
    if (Number.isFinite(d) && inView) return clamp(d);
  }
  if (missionCenter) {
    const d = distance(origin, missionCenter);
    if (Number.isFinite(d) && d >= 150) return clamp(d, 3000);
  }
  return WEDGE_DEFAULT_M;
}

/**
 * "CAM B · 35mm" (focal from the live state when the lens zoomed, else the configured lens). A zoom
 * reads "CAM B · 70–200mm", or "CAM B · 135mm" once its operator set the current focal ("my lens").
 */
export function cameraMapLabel(cam: (Pick<Camera, 'label'> & Partial<Pick<Camera, 'focalMinMm' | 'focalMaxMm'>>) | undefined, focalMm: number | null | undefined): string {
  const name = cam?.label?.trim() || 'CAM';
  const lo = cam?.focalMinMm;
  const hi = cam?.focalMaxMm;
  if (lo != null && hi != null && lo > 0 && hi > lo && !(focalMm && focalMm >= lo && focalMm < hi)) return `${name} · ${Math.round(lo)}–${Math.round(hi)}mm`;
  return focalMm && focalMm > 0 ? `${name} · ${Math.round(focalMm)}mm` : name;
}
